/**
 * CoachPlaybook - IndexedDB Utilities
 * Version: 0.6.0
 * Description: Database operations for plays, players, teams, and settings
 */

const DB_NAME = 'coachplaybook-db';
const DB_VERSION = 3;

let dbInstance = null;

// Store names constant
export const STORES = {
  PLAYS: 'plays',
  PLAYERS: 'players',
  TEAMS: 'teams',
  OPPONENTS: 'teams',  // Alias - opponents are stored in teams store
  SETTINGS: 'settings'
};

export const openDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains('plays')) {
        const playsStore = db.createObjectStore('plays', { keyPath: 'id' });
        playsStore.createIndex('type', 'type', { unique: false });
        playsStore.createIndex('name', 'name', { unique: false });
        playsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('players')) {
        const playersStore = db.createObjectStore('players', { keyPath: 'id' });
        playersStore.createIndex('number', 'number', { unique: false });
        playersStore.createIndex('lastName', 'lastName', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('teams')) {
        const teamsStore = db.createObjectStore('teams', { keyPath: 'id' });
        teamsStore.createIndex('name', 'name', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }
    };
  });
};

// Generic CRUD operations
export const getAll = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

export const getById = async (storeName, id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const save = async (storeName, item) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const remove = async (storeName, id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const clearStore = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Put all items in a store
export const putAll = async (storeName, items) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    items.forEach(item => store.put(item));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Plays-specific operations
export const getAllPlays = () => getAll('plays');
export const getPlayById = (id) => getById('plays', id);
export const savePlay = (play) => save('plays', { ...play, updatedAt: new Date().toISOString() });
export const deletePlay = (id) => remove('plays', id);

// Players-specific operations
export const getAllPlayers = () => getAll('players');
export const getPlayerById = (id) => getById('players', id);
export const savePlayer = (player) => save('players', player);
export const deletePlayer = (id) => remove('players', id);

// Teams-specific operations
export const getAllTeams = () => getAll('teams');
export const getTeamById = (id) => getById('teams', id);
export const saveTeam = (team) => save('teams', team);
export const deleteTeam = (id) => remove('teams', id);

// Settings operations
export const getSetting = async (key, defaultValue = null) => {
  try {
    const result = await getById('settings', key);
    return result ? result.value : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const setSetting = async (key, value) => {
  return save('settings', { key, value });
};

// ===== Sport-Scoped Operations =====
// Each record gets a sportId field. Queries filter by sport.
// Existing records without sportId are treated as 'basketball' (backwards compat).

/** Get all plays for a specific sport */
export const getPlaysBySport = async (sportId) => {
  const all = await getAll('plays');
  return all.filter(p => (p.sportId || 'basketball') === sportId);
};

/** Get all players for a specific sport */
export const getPlayersBySport = async (sportId) => {
  const all = await getAll('players');
  return all.filter(p => (p.sportId || 'basketball') === sportId);
};

/** Get all teams/opponents for a specific sport */
export const getTeamsBySport = async (sportId) => {
  const all = await getAll('teams');
  return all.filter(t => (t.sportId || 'basketball') === sportId);
};

/** Save a play with sport tag */
export const savePlayForSport = (play, sportId) => {
  return save('plays', { ...play, sportId, updatedAt: new Date().toISOString() });
};

/** Save a player with sport tag */
export const savePlayerForSport = (player, sportId) => {
  return save('players', { ...player, sportId });
};

/** Save a team with sport tag */
export const saveTeamForSport = (team, sportId) => {
  return save('teams', { ...team, sportId });
};

// Bulk operations
export const saveAllPlays = async (plays) => {
  return putAll('plays', plays.map(p => ({ ...p, updatedAt: new Date().toISOString() })));
};

export const saveAllPlayers = async (players) => {
  return putAll('players', players);
};

export const saveAllTeams = async (teams) => {
  return putAll('teams', teams);
};

// Estimate size of data
const estimateSize = (data) => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
};

// Storage stats - returns structure expected by DataManagement
export const getStorageStats = async () => {
  const plays = await getAllPlays();
  const players = await getAllPlayers();
  const teams = await getAllTeams();
  
  return {
    counts: {
      plays: plays.length,
      players: players.length,
      teams: teams.length,
      opponents: teams.length
    },
    sizes: {
      plays: estimateSize(plays),
      players: estimateSize(players),
      teams: estimateSize(teams),
      opponents: estimateSize(teams)
    },
    totalItems: plays.length + players.length + teams.length
  };
};

// Backup status
export const getBackupStatus = async () => {
  const lastBackup = await getSetting('lastBackupDate', null);
  const daysSinceBackup = lastBackup 
    ? Math.floor((Date.now() - new Date(lastBackup).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  
  let recommendation = 'Create your first backup';
  if (daysSinceBackup !== null) {
    if (daysSinceBackup === 0) recommendation = 'Backup is current';
    else if (daysSinceBackup < 7) recommendation = 'Backup is recent';
    else if (daysSinceBackup < 30) recommendation = 'Consider backing up soon';
    else recommendation = 'Backup recommended - over 30 days old';
  }
  
  return {
    lastBackup,
    daysSinceBackup,
    hasBackup: !!lastBackup,
    recommendation
  };
};

// Clear all data
export const clearAllData = async () => {
  try {
    await clearStore('plays');
    await clearStore('players');
    await clearStore('teams');
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Export all data for backup - returns downloadable file
export const exportAllData = async () => {
  try {
    const plays = await getAllPlays();
    const players = await getAllPlayers();
    const teams = await getAllTeams();
    
    const db = await openDB();
    const settings = await new Promise((resolve, reject) => {
      const transaction = db.transaction('settings', 'readonly');
      const store = transaction.objectStore('settings');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    
    const exportData = {
      plays,
      players,
      teams,
      settings,
      exportDate: new Date().toISOString(),
      version: '0.5.146'
    };
    
    // Create and download file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const fileName = `coachplaybook-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    // Update backup date
    await setSetting('lastBackupDate', new Date().toISOString());
    
    return { success: true, fileName };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Import data from file
export const importData = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        const itemsImported = {
          players: 0,
          plays: 0,
          opponents: 0
        };
        
        if (data.plays && Array.isArray(data.plays)) {
          await clearStore('plays');
          await saveAllPlays(data.plays);
          itemsImported.plays = data.plays.length;
        }
        
        if (data.players && Array.isArray(data.players)) {
          await clearStore('players');
          await saveAllPlayers(data.players);
          itemsImported.players = data.players.length;
        }
        
        if (data.teams && Array.isArray(data.teams)) {
          await clearStore('teams');
          await saveAllTeams(data.teams);
          itemsImported.opponents = data.teams.length;
        }
        
        if (data.settings && Array.isArray(data.settings)) {
          for (const setting of data.settings) {
            await save('settings', setting);
          }
        }
        
        resolve({ success: true, itemsImported });
      } catch (error) {
        reject({ success: false, error: error.message });
      }
    };
    
    reader.onerror = () => reject({ success: false, error: 'Failed to read file' });
    reader.readAsText(file);
  });
};

// Import all data (direct object, not file)
export const importAllData = async (data) => {
  const itemsImported = { players: 0, plays: 0, opponents: 0 };
  
  if (data.plays) {
    await clearStore('plays');
    await saveAllPlays(data.plays);
    itemsImported.plays = data.plays.length;
  }
  if (data.players) {
    await clearStore('players');
    await saveAllPlayers(data.players);
    itemsImported.players = data.players.length;
  }
  if (data.teams) {
    await clearStore('teams');
    await saveAllTeams(data.teams);
    itemsImported.opponents = data.teams.length;
  }
  if (data.settings) {
    for (const setting of data.settings) {
      await save('settings', setting);
    }
  }
  
  return { success: true, itemsImported };
};

export default {
  openDB,
  STORES,
  getAll,
  getById,
  save,
  remove,
  clearStore,
  putAll,
  getAllPlays,
  getPlayById,
  savePlay,
  deletePlay,
  getAllPlayers,
  getPlayerById,
  savePlayer,
  deletePlayer,
  getAllTeams,
  getTeamById,
  saveTeam,
  deleteTeam,
  getSetting,
  setSetting,
  saveAllPlays,
  saveAllPlayers,
  saveAllTeams,
  getStorageStats,
  getBackupStatus,
  clearAllData,
  exportAllData,
  importData,
  importAllData
};
